import { ImageZoom } from '../components/ImageZoom';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { cbseAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { HighlightableText } from '../components/HighlightableText';
import { SUBJECT_COLORS, cn, formatPercentage } from '../lib/utils';
import { toast } from 'sonner';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line
} from 'recharts';
import {
    Play, Clock, CheckCircle, XCircle, AlertTriangle, Trophy, 
    ArrowRight, ArrowLeft, Loader2, History, BarChart3, BookOpen,
    Timer, Flag, Eye
} from 'lucide-react';
import Layout from '../components/Layout';

// Exam Setup Component
function ExamSetup({ onStart }) {
    const [questionCount, setQuestionCount] = useState(50);
    const [timeSeconds, setTimeSeconds] = useState(50 * 90);
    const [loading, setLoading] = useState(false);

    // Auto-set time to 90 seconds per question when question count changes
    const handleQuestionCountChange = (v) => {
        setQuestionCount(v);
        setTimeSeconds(v * 90);
    };

    const timeMinutes = (timeSeconds / 60).toFixed(timeSeconds % 60 === 0 ? 0 : 0);

    const handleStart = async () => {
        setLoading(true);
        try {
            await onStart(questionCount, Math.ceil(timeSeconds / 60));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card className="border-slate-200 max-w-2xl mx-auto">
            <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <Trophy className="h-8 w-8 text-indigo-600" />
                </div>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
                    CBSE Practice Exam
                </CardTitle>
                <CardDescription>
                    Simulate the Comprehensive Basic Science Examination with weighted questions from all subjects
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="space-y-6">
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-slate-700">Number of Questions</label>
                            <span className="text-sm font-bold text-indigo-600" data-testid="cbse-question-count-label">{questionCount}</span>
                        </div>
                        <Slider
                            value={[questionCount]}
                            onValueChange={([v]) => handleQuestionCountChange(v)}
                            min={5}
                            max={200}
                            step={5}
                            data-testid="cbse-question-slider"
                        />
                        <div className="flex justify-between text-xs text-slate-400">
                            <span>5</span>
                            <span>100</span>
                            <span>200</span>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-slate-700">Time Limit</label>
                            <span className="text-sm font-bold text-indigo-600" data-testid="cbse-time-label">{timeMinutes} minutes</span>
                        </div>
                        <Slider
                            value={[timeSeconds]}
                            onValueChange={([v]) => setTimeSeconds(v)}
                            min={450}
                            max={18000}
                            step={30}
                            data-testid="cbse-time-slider"
                        />
                        <div className="flex justify-between text-xs text-slate-400">
                            <span>7.5 min</span>
                            <span>75 min</span>
                            <span>150 min</span>
                            <span>300 min</span>
                        </div>
                    </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-medium text-slate-900 mb-2">NBME CBSE Discipline Distribution</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                        <div>• Pathology: 45–55%</div>
                        <div>• Physiology: 30–40%</div>
                        <div>• Nutrition: 15–20%</div>
                        <div>• Anatomy & Embryology: 10–20%</div>
                        <div>• Microbiology: 10–20%</div>
                        <div>• Pharmacology: 10–20%</div>
                        <div>• Behavioral Sciences: 10–15%</div>
                        <div>• Biochemistry: 5–15%</div>
                        <div>• Histology & Cell Bio: 5–15%</div>
                        <div>• Immunology: 5–15%</div>
                        <div>• Genetics: 5–10%</div>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">*Ranges overlap — questions may test multiple disciplines</p>
                </div>

                <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <p className="text-sm text-amber-800">
                        Questions will be generated by AI. This may take several minutes for larger exams.
                    </p>
                </div>

                <Button 
                    onClick={handleStart} 
                    disabled={loading}
                    className="w-full"
                    size="lg"
                    data-testid="cbse-start-btn"
                >
                    {loading ? (
                        <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Starting Exam...
                        </>
                    ) : (
                        <>
                            <Play className="mr-2 h-5 w-5" />
                            Start CBSE Exam
                        </>
                    )}
                </Button>
            </CardContent>
        </Card>
    );
}

// Exam In Progress Component
function ExamInProgress({ examId, questions, timeLimit, onSubmit, onAbandon }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [flagged, setFlagged] = useState(new Set());
    const [timeRemaining, setTimeRemaining] = useState(timeLimit * 60);
    const [showSubmitDialog, setShowSubmitDialog] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const startTimeRef = useRef(Date.now());

    // Timer
    useEffect(() => {
        const interval = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    handleSubmit();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        if (hrs > 0) {
            return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const currentQuestion = questions[currentIndex]?.question;
    const answeredCount = Object.keys(answers).length;
    const progress = (answeredCount / questions.length) * 100;

    const selectAnswer = async (letter) => {
        const questionId = currentQuestion.id;
        setAnswers(prev => ({ ...prev, [questionId]: letter }));
        
        // Save to backend
        try {
            await cbseAPI.saveAnswer(examId, questionId, letter);
        } catch (err) {
            console.error('Failed to save answer:', err);
        }
    };

    const toggleFlag = () => {
        const qId = currentQuestion.id;
        setFlagged(prev => {
            const newSet = new Set(prev);
            if (newSet.has(qId)) {
                newSet.delete(qId);
            } else {
                newSet.add(qId);
            }
            return newSet;
        });
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
        const answersList = Object.entries(answers).map(([questionId, selectedAnswer]) => ({
            question_id: questionId,
            selected_answer: selectedAnswer
        }));
        
        try {
            const result = await onSubmit(answersList, timeSpent);
            toast.success('Exam submitted successfully!');
        } catch (err) {
            toast.error('Failed to submit exam');
        } finally {
            setSubmitting(false);
            setShowSubmitDialog(false);
        }
    };

    const unansweredCount = questions.length - answeredCount;
    const isLowTime = timeRemaining < 300; // Less than 5 minutes

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="sticky top-16 z-40 bg-white border-b border-slate-200 -mx-4 px-4 py-3 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-4">
                        <Badge variant="outline" className="text-lg px-3 py-1">
                            {currentIndex + 1} / {questions.length}
                        </Badge>
                        <div className="hidden sm:block">
                            <Progress value={progress} className="w-32 h-2" />
                        </div>
                    </div>
                    
                    <div className={cn(
                        "flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-lg",
                        isLowTime ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"
                    )}>
                        <Timer className="h-5 w-5" />
                        {formatTime(timeRemaining)}
                    </div>

                    <div className="flex items-center gap-2">
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setShowSubmitDialog(true)}
                            data-testid="cbse-submit-btn"
                        >
                            Submit Exam
                        </Button>
                    </div>
                </div>
            </div>

            {/* Question */}
            {currentQuestion && (
                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Main Question Area */}
                    <div className="lg:col-span-2 space-y-4">
                        <Card className="border-slate-200">
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Badge className={cn(
                                            SUBJECT_COLORS[currentQuestion.subject]?.bg,
                                            SUBJECT_COLORS[currentQuestion.subject]?.text
                                        )}>
                                            {currentQuestion.subject}
                                        </Badge>
                                        <Badge variant="outline">{currentQuestion.difficulty}</Badge>
                                    </div>
                                    <Button
                                        variant={flagged.has(currentQuestion.id) ? "default" : "ghost"}
                                        size="sm"
                                        onClick={toggleFlag}
                                        className={flagged.has(currentQuestion.id) ? "bg-amber-500 hover:bg-amber-600" : ""}
                                    >
                                        <Flag className="h-4 w-4 mr-1" />
                                        {flagged.has(currentQuestion.id) ? "Flagged" : "Flag"}
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Medical Image - no description shown during exam */}
                                {currentQuestion.image_base64 && (
                                    <div className="rounded-lg overflow-hidden border-2 border-slate-300 bg-white">
                                        <ImageZoom 
                                            src={`data:image/png;base64,${currentQuestion.image_base64}`}
                                            alt="Medical image for examination"
                                        />
                                    </div>
                                )}

                                {/* Vignette */}
                                <div className="p-4 rounded-lg bg-slate-50">
                                    <HighlightableText 
                                        text={currentQuestion.vignette}
                                        className="text-slate-700 leading-relaxed"
                                    />
                                </div>

                                {/* Question Stem */}
                                <p className="font-medium text-slate-900">
                                    {currentQuestion.question_stem}
                                </p>

                                {/* Options */}
                                <div className="space-y-2">
                                    {currentQuestion.options.map((option) => {
                                        const isSelected = answers[currentQuestion.id] === option.letter;
                                        return (
                                            <div
                                                key={option.letter}
                                                onClick={() => selectAnswer(option.letter)}
                                                className={cn(
                                                    "flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all",
                                                    isSelected 
                                                        ? "border-indigo-500 bg-indigo-50" 
                                                        : "border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50"
                                                )}
                                                data-testid={`cbse-option-${option.letter}`}
                                            >
                                                <div className={cn(
                                                    "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm",
                                                    isSelected ? "bg-indigo-600 text-white" : "bg-slate-200 text-slate-600"
                                                )}>
                                                    {option.letter}
                                                </div>
                                                <span className="flex-1">{option.text}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Navigation */}
                        <div className="flex items-center justify-between">
                            <Button
                                variant="outline"
                                onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
                                disabled={currentIndex === 0}
                            >
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Previous
                            </Button>
                            <Button
                                onClick={() => setCurrentIndex(prev => Math.min(questions.length - 1, prev + 1))}
                                disabled={currentIndex === questions.length - 1}
                            >
                                Next
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </div>
                    </div>

                    {/* Question Navigator */}
                    <div className="lg:col-span-1">
                        <Card className="border-slate-200 sticky top-36">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm">Question Navigator</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-5 gap-2 max-h-64 overflow-y-auto">
                                    {questions.map((q, idx) => {
                                        const qId = q.question.id;
                                        const isAnswered = answers[qId];
                                        const isFlagged = flagged.has(qId);
                                        const isCurrent = idx === currentIndex;
                                        
                                        return (
                                            <button
                                                key={idx}
                                                onClick={() => setCurrentIndex(idx)}
                                                className={cn(
                                                    "w-10 h-10 rounded-lg text-sm font-medium transition-all",
                                                    isCurrent && "ring-2 ring-indigo-500",
                                                    isAnswered && !isFlagged && "bg-emerald-100 text-emerald-700",
                                                    isFlagged && "bg-amber-100 text-amber-700",
                                                    !isAnswered && !isFlagged && "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                                )}
                                            >
                                                {idx + 1}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="mt-4 pt-4 border-t border-slate-200 space-y-2 text-sm">
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded bg-emerald-100"></div>
                                        <span>Answered: {answeredCount}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded bg-amber-100"></div>
                                        <span>Flagged: {flagged.size}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 rounded bg-slate-100"></div>
                                        <span>Unanswered: {unansweredCount}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            )}

            {/* Submit Dialog */}
            <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Submit Exam?</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to submit your exam?
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-2">
                        <div className="flex justify-between">
                            <span>Questions Answered:</span>
                            <span className="font-semibold">{answeredCount} / {questions.length}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Unanswered:</span>
                            <span className="font-semibold text-amber-600">{unansweredCount}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Flagged for Review:</span>
                            <span className="font-semibold">{flagged.size}</span>
                        </div>
                    </div>
                    {unansweredCount > 0 && (
                        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
                            You have {unansweredCount} unanswered questions. Unanswered questions will be marked incorrect.
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>
                            Continue Exam
                        </Button>
                        <Button onClick={handleSubmit} disabled={submitting}>
                            {submitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Submitting...
                                </>
                            ) : (
                                'Submit Exam'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Main CBSE Component
export default function CBSE() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('exam');
    const [examState, setExamState] = useState('setup'); // 'setup', 'loading', 'in_progress', 'completed'
    const [currentExam, setCurrentExam] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState([]);
    const [stats, setStats] = useState(null);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [genProgress, setGenProgress] = useState({ generated: 0, total: 0 });
    const pollRef = useRef(null);

    useEffect(() => {
        checkActiveExam();
    }, []);

    useEffect(() => {
        if (activeTab === 'history') {
            fetchHistory();
        } else if (activeTab === 'stats') {
            fetchStats();
        }
    }, [activeTab]);

    useEffect(() => {
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, []);

    const checkActiveExam = async () => {
        try {
            const res = await cbseAPI.getActive();
            if (res.data.active) {
                const exam = res.data;
                setCurrentExam({
                    id: exam.exam_id,
                    questionCount: exam.question_count,
                    timeLimit: exam.time_limit_minutes,
                });
                if (exam.generation_status === 'completed') {
                    // Exam ready — load questions and resume
                    setExamState('loading');
                    setGenProgress({ generated: exam.questions_generated, total: exam.question_count });
                    try {
                        const questionsResponse = await cbseAPI.getQuestions(exam.exam_id);
                        setQuestions(questionsResponse.data.questions);
                        setExamState('in_progress');
                    } catch {
                        // If questions can't load, show resume prompt
                        setExamState('resume_prompt');
                    }
                } else if (exam.generation_status === 'generating') {
                    // Still generating — start polling
                    setExamState('loading');
                    setGenProgress({ generated: exam.questions_generated, total: exam.question_count });
                    startProgressPolling(exam.exam_id, exam.question_count);
                } else {
                    // Failed or unknown — let user abandon
                    setExamState('resume_prompt');
                }
            }
        } catch (err) {
            console.error('Failed to check active exam:', err);
        }
    };

    const startProgressPolling = (examId, questionCount) => {
        const pollProgress = async () => {
            try {
                const prog = await cbseAPI.getProgress(examId);
                const { questions_generated, generation_status } = prog.data;
                setGenProgress({ generated: questions_generated, total: questionCount });

                if (generation_status === 'completed') {
                    clearInterval(pollRef.current);
                    const questionsResponse = await cbseAPI.getQuestions(examId);
                    setQuestions(questionsResponse.data.questions);
                    setExamState('in_progress');
                    toast.success('Exam ready! Good luck!');
                } else if (generation_status === 'failed') {
                    clearInterval(pollRef.current);
                    toast.error(prog.data.generation_error || 'Question generation failed');
                    setExamState('setup');
                }
            } catch (err) {
                console.error('Progress poll error:', err);
            }
        };
        pollRef.current = setInterval(pollProgress, 2000);
        pollProgress();
    };

    const fetchHistory = async () => {
        setLoadingHistory(true);
        try {
            const response = await cbseAPI.getHistory(20);
            setHistory(response.data.history);
        } catch (err) {
            console.error('Failed to fetch history:', err);
        } finally {
            setLoadingHistory(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await cbseAPI.getStats();
            setStats(response.data);
        } catch (err) {
            console.error('Failed to fetch stats:', err);
        }
    };

    const startExam = async (questionCount, timeLimit) => {
        try {
            setExamState('loading');
            setGenProgress({ generated: 0, total: questionCount });
            
            const startResponse = await cbseAPI.startExam(questionCount, timeLimit);
            const examId = startResponse.data.exam_id;
            const genStatus = startResponse.data.generation_status;
            
            setCurrentExam({ id: examId, questionCount, timeLimit });
            
            if (genStatus === 'completed') {
                // Pool had enough — instant start!
                setGenProgress({ generated: questionCount, total: questionCount });
                const questionsResponse = await cbseAPI.getQuestions(examId);
                setQuestions(questionsResponse.data.questions);
                setExamState('in_progress');
                toast.success('Exam ready! Good luck!');
            } else {
                // Pool shortfall — poll for remaining
                setGenProgress({ generated: startResponse.data.questions_ready || 0, total: questionCount });
                toast.info('Generating remaining questions...');
                startProgressPolling(examId, questionCount);
            }
            
        } catch (err) {
            console.error('Failed to start exam:', err);
            toast.error(err.response?.data?.detail || 'Failed to start exam');
            setExamState('setup');
        }
    };

    const submitExam = async (answers, timeSpent) => {
        try {
            const response = await cbseAPI.submitExam(currentExam.id, answers, timeSpent);
            setResult(response.data);
            setExamState('completed');
            return response.data;
        } catch (err) {
            throw err;
        }
    };

    const abandonExam = async () => {
        if (pollRef.current) clearInterval(pollRef.current);
        if (currentExam) {
            try {
                await cbseAPI.abandonExam(currentExam.id);
            } catch (err) {
                console.error('Failed to abandon exam:', err);
            }
        }
        setExamState('setup');
        setCurrentExam(null);
        setQuestions([]);
    };

    const resetExam = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        setExamState('setup');
        setCurrentExam(null);
        setQuestions([]);
        setResult(null);
    };

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="cbse-container">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                            CBSE Exam
                        </h1>
                        <p className="text-slate-500 mt-1">Comprehensive Basic Science Examination Practice</p>
                    </div>
                </div>

                {examState === 'resume_prompt' && currentExam && (
                    <Card className="border-amber-200 bg-amber-50 max-w-md mx-auto" data-testid="cbse-resume-prompt">
                        <CardContent className="p-8 text-center space-y-4">
                            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto" />
                            <h3 className="text-lg font-semibold">Exam In Progress</h3>
                            <p className="text-sm text-slate-600">
                                You have an unfinished CBSE exam. Would you like to continue or start fresh?
                            </p>
                            <div className="flex gap-3 justify-center">
                                <Button
                                    onClick={async () => {
                                        setExamState('loading');
                                        setGenProgress({ generated: 0, total: currentExam.questionCount });
                                        try {
                                            const prog = await cbseAPI.getProgress(currentExam.id);
                                            if (prog.data.generation_status === 'completed') {
                                                const qRes = await cbseAPI.getQuestions(currentExam.id);
                                                setQuestions(qRes.data.questions);
                                                setExamState('in_progress');
                                            } else if (prog.data.generation_status === 'generating') {
                                                startProgressPolling(currentExam.id, currentExam.questionCount);
                                            } else {
                                                toast.error('This exam cannot be resumed.');
                                                abandonExam();
                                            }
                                        } catch {
                                            toast.error('Failed to resume exam.');
                                            abandonExam();
                                        }
                                    }}
                                    data-testid="cbse-resume-btn"
                                >
                                    <Play className="h-4 w-4 mr-2" /> Continue Exam
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={abandonExam}
                                    data-testid="cbse-abandon-btn"
                                >
                                    Abandon & Start New
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}


                {examState === 'setup' && (
                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                        <TabsList className="bg-slate-100">
                            <TabsTrigger value="exam">
                                <Play className="h-4 w-4 mr-2" />
                                New Exam
                            </TabsTrigger>
                            <TabsTrigger value="history">
                                <History className="h-4 w-4 mr-2" />
                                History
                            </TabsTrigger>
                            <TabsTrigger value="stats">
                                <BarChart3 className="h-4 w-4 mr-2" />
                                Statistics
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="exam" className="mt-6">
                            <ExamSetup onStart={startExam} />
                        </TabsContent>

                        <TabsContent value="history" className="mt-6">
                            {loadingHistory ? (
                                <div className="flex justify-center py-12">
                                    <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                                </div>
                            ) : history.length > 0 ? (
                                <div className="space-y-4">
                                    {history.map((exam, idx) => (
                                        <Card key={exam.exam_id} className="border-slate-200">
                                            <CardContent className="p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-4">
                                                        <div className={cn(
                                                            "w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg",
                                                            exam.score_percentage >= 70 
                                                                ? "bg-emerald-100 text-emerald-700"
                                                                : exam.score_percentage >= 50
                                                                ? "bg-amber-100 text-amber-700"
                                                                : "bg-red-100 text-red-700"
                                                        )}>
                                                            {Math.round(exam.score_percentage)}%
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-slate-900">
                                                                {exam.correct_answers} / {exam.total_questions} correct
                                                            </p>
                                                            <p className="text-sm text-slate-500">
                                                                {new Date(exam.completed_at).toLocaleDateString()} • {exam.time_spent_minutes} min
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => navigate(`/cbse/review/${exam.exam_id}`)}
                                                    >
                                                        <Eye className="h-4 w-4 mr-1" />
                                                        Review
                                                    </Button>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <Card className="border-slate-200 border-dashed">
                                    <CardContent className="p-12 text-center">
                                        <History className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                                        <p className="text-slate-500">No exam history yet. Start your first CBSE exam!</p>
                                    </CardContent>
                                </Card>
                            )}
                        </TabsContent>

                        <TabsContent value="stats" className="mt-6">
                            {stats ? (
                                <div className="space-y-6">
                                    {/* Stats Cards */}
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <Card className="border-slate-200">
                                            <CardContent className="p-4">
                                                <p className="text-sm text-slate-500">Total Exams</p>
                                                <p className="text-2xl font-bold">{stats.total_exams}</p>
                                            </CardContent>
                                        </Card>
                                        <Card className="border-slate-200">
                                            <CardContent className="p-4">
                                                <p className="text-sm text-slate-500">Average Score</p>
                                                <p className="text-2xl font-bold">{stats.average_score}%</p>
                                            </CardContent>
                                        </Card>
                                        <Card className="border-slate-200">
                                            <CardContent className="p-4">
                                                <p className="text-sm text-slate-500">Best Score</p>
                                                <p className="text-2xl font-bold text-emerald-600">{stats.best_score}%</p>
                                            </CardContent>
                                        </Card>
                                        <Card className="border-slate-200">
                                            <CardContent className="p-4">
                                                <p className="text-sm text-slate-500">Questions Attempted</p>
                                                <p className="text-2xl font-bold">{stats.total_questions_attempted}</p>
                                            </CardContent>
                                        </Card>
                                    </div>

                                    {/* Score Trend */}
                                    {stats.score_trend.length > 0 && (
                                        <Card className="border-slate-200">
                                            <CardHeader>
                                                <CardTitle>Score Trend</CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <ResponsiveContainer width="100%" height={200}>
                                                    <LineChart data={stats.score_trend}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                                        <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                                                        <Tooltip />
                                                        <Line 
                                                            type="monotone" 
                                                            dataKey="score" 
                                                            stroke="#4F46E5" 
                                                            strokeWidth={2}
                                                            dot={{ fill: '#4F46E5' }}
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </CardContent>
                                        </Card>
                                    )}

                                    {/* Subject Performance */}
                                    <Card className="border-slate-200">
                                        <CardHeader>
                                            <CardTitle>Performance by Subject</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <ResponsiveContainer width="100%" height={250}>
                                                <BarChart data={Object.entries(stats.by_subject).map(([name, data]) => ({
                                                    name: name.substring(0, 4),
                                                    fullName: name,
                                                    accuracy: data.accuracy,
                                                    total: data.total
                                                }))}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                                    <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                                                    <Tooltip 
                                                        content={({ active, payload }) => {
                                                            if (active && payload && payload.length) {
                                                                const data = payload[0].payload;
                                                                return (
                                                                    <div className="bg-white p-3 rounded-lg shadow-lg border">
                                                                        <p className="font-medium">{data.fullName}</p>
                                                                        <p className="text-sm">Accuracy: {data.accuracy}%</p>
                                                                        <p className="text-sm">Questions: {data.total}</p>
                                                                    </div>
                                                                );
                                                            }
                                                            return null;
                                                        }}
                                                    />
                                                    <Bar dataKey="accuracy" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </CardContent>
                                    </Card>
                                </div>
                            ) : (
                                <div className="flex justify-center py-12">
                                    <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>
                )}

                {examState === 'loading' && (
                    <Card className="border-slate-200 max-w-md mx-auto" data-testid="cbse-loading">
                        <CardContent className="p-10 text-center space-y-6">
                            <div className="relative mx-auto w-16 h-16">
                                <div className="absolute inset-0 border-4 border-indigo-100 rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold mb-1">Generating Questions</h3>
                                <p className="text-sm text-slate-500 mb-4">
                                    {genProgress.generated} of {genProgress.total} questions ready
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Progress 
                                    value={genProgress.total > 0 ? (genProgress.generated / genProgress.total) * 100 : 0} 
                                    className="h-3"
                                    data-testid="cbse-progress-bar"
                                />
                                <p className="text-xs text-slate-400">
                                    {genProgress.total > 0 
                                        ? `${Math.round((genProgress.generated / genProgress.total) * 100)}% complete`
                                        : 'Starting...'}
                                </p>
                            </div>
                            <Button variant="outline" size="sm" onClick={abandonExam} data-testid="cbse-cancel-generation">
                                Cancel
                            </Button>
                        </CardContent>
                    </Card>
                )}

                {examState === 'in_progress' && currentExam && (
                    <ExamInProgress
                        examId={currentExam.id}
                        questions={questions}
                        timeLimit={currentExam.timeLimit}
                        onSubmit={submitExam}
                        onAbandon={abandonExam}
                    />
                )}

                {examState === 'completed' && result && (
                    <Card className="border-slate-200 max-w-2xl mx-auto">
                        <CardHeader className="text-center">
                            <div className={cn(
                                "mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-4",
                                result.score_percentage >= 70 
                                    ? "bg-emerald-100" 
                                    : result.score_percentage >= 50 
                                    ? "bg-amber-100" 
                                    : "bg-red-100"
                            )}>
                                {result.score_percentage >= 70 ? (
                                    <Trophy className="h-10 w-10 text-emerald-600" />
                                ) : (
                                    <BookOpen className="h-10 w-10 text-amber-600" />
                                )}
                            </div>
                            <CardTitle className="text-3xl" style={{ fontFamily: 'Manrope' }}>
                                {result.score_percentage >= 70 ? 'Great Job!' : 'Exam Complete'}
                            </CardTitle>
                            <CardDescription>
                                You scored {result.score_percentage}% on this CBSE practice exam
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div className="p-4 rounded-lg bg-emerald-50">
                                    <p className="text-2xl font-bold text-emerald-700">{result.correct_answers}</p>
                                    <p className="text-sm text-emerald-600">Correct</p>
                                </div>
                                <div className="p-4 rounded-lg bg-red-50">
                                    <p className="text-2xl font-bold text-red-700">{result.incorrect_answers}</p>
                                    <p className="text-sm text-red-600">Incorrect</p>
                                </div>
                                <div className="p-4 rounded-lg bg-slate-50">
                                    <p className="text-2xl font-bold text-slate-700">{result.unanswered}</p>
                                    <p className="text-sm text-slate-600">Unanswered</p>
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <Button 
                                    variant="outline" 
                                    className="flex-1"
                                    onClick={() => navigate(`/cbse/review/${result.exam_id}`)}
                                >
                                    <Eye className="mr-2 h-4 w-4" />
                                    Review Answers
                                </Button>
                                <Button 
                                    className="flex-1"
                                    onClick={resetExam}
                                >
                                    <Play className="mr-2 h-4 w-4" />
                                    New Exam
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
}
